/*
  InStyle — play the shopping pages' looping videos when they are on screen,
  and keep them playing however long the page stays open.
  ------------------------------------------------------------------
  Plays every `video[data-instyle-autoplay]` (rendered by
  snippets/instyle-media-video.liquid) once at least 40% of it is visible, and
  pauses it when it leaves. Nothing loads before that: the videos carry
  `preload="none"` and NO `autoplay` attribute, so a visitor who never scrolls
  to one pays no bytes for it.

  A refused play() — iOS Low Power Mode, a data saver — is swallowed and the
  poster simply stays. The poster is the film's first frame, so a phone that
  refuses autoplay shows a still picture, never a black box.

  Not scroll-driven: no scroll lock, no scrubbing, no flick. Rules 1-4, 7-10
  and 13-16 of context/scroll-animation-rules.md concern scrubbed films and do
  not apply. These do, ungated on every device (rule 11):
    R5  never one media event as the only escape — every event below is
        paired with a periodic sweep
    R6  a tab coming back (visibilitychange, and pageshow for bfcache) replays
        whatever is still on screen; a backgrounded tab can drop playback

  KEEPALIVE (2026-10-02, Naman: "make sure that all the videos … don't get
  lagged just because the website is open for a long time, or get unloaded").
  Every film this file plays is watched by one sweep (every 1.5 s, only while
  the tab is visible), which repairs each way a film can die:

    paused by the browser with no event    -> played again (not after a
                                              refusal: that waits for a fresh
                                              reason, so it is never a loop)
    playing but frozen (network stall, a   -> after 5 s with neither the
      decoder that stopped)                   picture nor the download moving:
                                              load() + seek back + play(), with
                                              the wait doubling (5, 10, 20 s),
                                              three tries, then give up
    failed to load (error, no source)      -> one retry after 3 s, then give up
    readyState 0 after it had played       -> load() before play()
      (the browser dropped its buffer)
    gave up                                -> back to the poster (no data =
                                              the poster shows), nothing more
                                              fetched until a fresh reason

  A FRESH REASON — the film scrolling back into view, the tab coming back, a
  back/forward-cache restore, the network coming back online — clears every
  counter and tries again from the top. So a hiccup heals on the next look,
  and a dead network costs a few bounded tries, never a loop.

  The tab hiding pauses every film (the browser does it anyway; doing it
  ourselves makes the resume deterministic). Showing it again resumes the ones
  on screen, from where they were.

  Other players share this: `InStyleAutoplay.keep(video, { wanted, start })`
  watches a video this file did not render (assets/instyle-category-hero.js
  uses it). `wanted()` says whether it should be playing now; `start()` is the
  player's own play routine and returns play()'s promise. keep() returns
  `{ revive }`, for the player to call on its own fresh reasons.

  Several sections on one page include this file, and each <script> tag runs,
  so the second run only re-scans.
*/
(() => {
  'use strict';

  if (window.InStyleAutoplay) {
    window.InStyleAutoplay.scan(document);
    return;
  }

  // Reduced motion: the poster is the whole picture. Never fetch a film.
  const REDUCED =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SELECTOR = 'video[data-instyle-autoplay]';
  const onScreen = new Set();

  if (!('IntersectionObserver' in window)) {
    window.InStyleAutoplay = { scan: () => {} };
    return;
  }

  /* ---------------------------------------------------------------- keepalive */

  const SWEEP_MS = 1500;
  const STALL_MS = 5000; // doubles per try: 5 s, 10 s, 20 s
  const MAX_NUDGES = 3;
  const ERROR_RETRY_MS = 3000;
  const MAX_ERROR_RETRIES = 1;
  const NETWORK_LOADING = 2;
  const NETWORK_NO_SOURCE = 3;

  const kept = new Map(); // video -> state
  let sweepTimer = 0;
  let io = null;

  const now = () => Date.now();

  // A `src` attribute that fails sets `error`; failed <source> children only
  // leave networkState at NETWORK_NO_SOURCE. Either way nothing will play.
  const failed = (v) => !!v.error || v.networkState === NETWORK_NO_SOURCE;

  // Had a picture once, holds nothing now, and is not fetching: the browser
  // emptied it. play() alone would sit on nothing.
  const emptied = (v, s) =>
    s.hadData && v.readyState === 0 && v.networkState !== NETWORK_LOADING;

  const bufferedEnd = (v) => {
    try {
      const b = v.buffered;
      return b && b.length ? b.end(b.length - 1) : 0;
    } catch (e) {
      return 0;
    }
  };

  // One play() with its rejection caught. Only a NotAllowedError is a
  // refusal; an AbortError is our own pause() or load() interrupting it.
  function attempt(v, s) {
    let p;
    try {
      p = s.start(v);
    } catch (e) {
      return;
    }
    if (p && typeof p.then === 'function') {
      p.then(
        () => {
          s.refused = false;
        },
        (err) => {
          if (err && err.name === 'NotAllowedError') s.refused = true;
        }
      );
    }
  }

  // Reload from the network, coming back to where it was once metadata is in.
  function reload(v, s) {
    const t = v.currentTime || 0;
    const d = v.duration;
    s.resumeAt = t > 0.25 && isFinite(d) && t < d - 0.25 ? t : 0;
    try {
      v.load();
    } catch (e) {
      /* nothing to reload */
    }
    s.lastT = v.currentTime;
    s.lastBuf = 0;
    s.movedAt = now();
    s.dataAt = now();
  }

  // Back to the poster: an element holding no data shows its poster. The
  // sweep leaves a film it gave up on alone until a fresh reason; preload none
  // asks the browser not to fetch more (Chrome may still open one request for
  // the reset, measured 2026-10-02, and nothing after it).
  function giveUp(v, s) {
    s.gaveUp = true;
    clearTimeout(s.retryTimer);
    s.retryTimer = 0;
    s.resumeAt = 0;
    try {
      v.pause();
    } catch (e) {
      /* already stopped */
    }
    v.preload = 'none';
    // A failed load holds no data and already shows the poster; reloading it
    // would only fetch the broken file once more. A frozen film needs the
    // reset to drop its last frame.
    if (failed(v)) return;
    try {
      v.load();
    } catch (e) {
      /* nothing to unload */
    }
  }

  function onError(v, s) {
    if (s.gaveUp || s.retryTimer) return;
    if (s.errors >= MAX_ERROR_RETRIES) {
      giveUp(v, s);
      return;
    }
    s.errors++;
    s.retryTimer = setTimeout(() => {
      s.retryTimer = 0;
      // Off screen or hidden by now: the next fresh reason retries it instead.
      if (document.hidden || !v.isConnected || !s.wanted()) return;
      reload(v, s);
      attempt(v, s);
    }, ERROR_RETRY_MS);
  }

  // A fresh reason to play: clear every counter and try from the top.
  function revive(v, s) {
    if (document.hidden || !v.isConnected || !s.wanted()) return;
    s.nudges = 0;
    s.errors = 0;
    s.gaveUp = false;
    s.refused = false;
    clearTimeout(s.retryTimer);
    s.retryTimer = 0;
    if (failed(v) || emptied(v, s)) {
      reload(v, s);
    } else {
      s.lastT = v.currentTime;
      s.movedAt = now();
      s.dataAt = now();
    }
    attempt(v, s);
  }

  function check(v, s, t) {
    if (!v.isConnected) {
      forget(v);
      return;
    }
    if (!s.wanted()) {
      if (!v.paused) v.pause();
      s.movedAt = t;
      return;
    }
    if (s.gaveUp) return;
    if (failed(v)) {
      onError(v, s);
      return;
    }
    if (v.readyState >= 2) s.hadData = true;

    // Bytes still arriving means it is slow, not stuck.
    const buf = bufferedEnd(v);
    if (buf !== s.lastBuf) {
      s.lastBuf = buf;
      s.dataAt = t;
    }

    if (v.paused || v.ended) {
      // Paused with no event: an OS media interruption, a resume that never
      // came. Not after a refusal, which waits for a fresh reason.
      s.movedAt = t;
      if (s.refused) return;
      if (emptied(v, s)) reload(v, s);
      attempt(v, s);
      return;
    }

    if (v.currentTime !== s.lastT) {
      s.lastT = v.currentTime;
      s.movedAt = t;
      if (s.nudges && t - s.nudgedAt > 30000) s.nudges = 0; // healthy again
      return;
    }

    // Frozen while it should be playing, and the download is not moving
    // either: a stall. Nudge it, waiting longer each time.
    const wait = STALL_MS * Math.pow(2, Math.min(s.nudges, 2));
    if (t - s.movedAt < wait || t - s.dataAt < wait) return;
    if (s.nudges >= MAX_NUDGES) {
      giveUp(v, s);
      return;
    }
    s.nudges++;
    s.nudgedAt = t;
    reload(v, s);
    attempt(v, s);
  }

  function sweep() {
    if (document.hidden) return;
    const t = now();
    kept.forEach((s, v) => check(v, s, t));
  }

  function forget(v) {
    const s = kept.get(v);
    if (s) clearTimeout(s.retryTimer);
    kept.delete(v);
    onScreen.delete(v);
    if (io) io.unobserve(v);
    if (!kept.size && sweepTimer) {
      clearInterval(sweepTimer);
      sweepTimer = 0;
    }
  }

  function keep(v, opts) {
    if (!v || REDUCED) return { revive: () => {} };
    let s = kept.get(v);
    if (!s) {
      s = {
        wanted: (opts && opts.wanted) || (() => false),
        start: (opts && opts.start) || ((el) => el.play()),
        nudges: 0,
        nudgedAt: 0,
        errors: 0,
        retryTimer: 0,
        refused: false,
        gaveUp: false,
        hadData: false,
        resumeAt: 0,
        lastT: 0,
        lastBuf: 0,
        movedAt: now(),
        dataAt: now()
      };
      kept.set(v, s);
      const st = s;
      // Errors on a <source> do not bubble; a capturing listener on the video
      // sees them. The sweep checks the same state, so this is not the only way.
      v.addEventListener('error', () => onError(v, st), true);
      v.addEventListener('progress', () => {
        st.dataAt = now();
      });
      v.addEventListener('loadeddata', () => {
        st.hadData = true;
      });
      v.addEventListener('loadedmetadata', () => {
        if (!st.resumeAt) return;
        const at = st.resumeAt;
        st.resumeAt = 0;
        if (isFinite(v.duration) && at < v.duration) {
          try {
            v.currentTime = at;
          } catch (e) {
            /* it simply resumes from the start of the loop */
          }
        }
      });
    }
    if (!sweepTimer) sweepTimer = setInterval(sweep, SWEEP_MS);
    const st = s;
    return { revive: () => revive(v, st) };
  }

  const reviveAll = () => {
    if (document.hidden) return;
    kept.forEach((s, v) => revive(v, s));
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      kept.forEach((s, v) => {
        if (!v.paused) v.pause();
      });
      return;
    }
    reviveAll();
  });
  // A bfcache restore fires neither visibilitychange nor load.
  window.addEventListener('pageshow', reviveAll);
  window.addEventListener('online', reviveAll);

  /* ------------------------------------------------------- on-screen playing */

  const playOwn = (v) => {
    if (v.preload === 'none') v.preload = 'auto';
    return v.play();
  };

  io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const v = e.target;
        if (e.isIntersecting && e.intersectionRatio >= 0.4) {
          onScreen.add(v);
          const s = kept.get(v);
          if (s) revive(v, s);
        } else {
          onScreen.delete(v);
          if (!v.paused) v.pause();
        }
      }
    },
    { threshold: [0, 0.4] }
  );

  const scan = (root = document) => {
    if (REDUCED || !root || !root.querySelectorAll) return;
    const found = root.matches && root.matches(SELECTOR) ? [root] : [];
    root.querySelectorAll(SELECTOR).forEach((v) => found.push(v));
    for (const v of found) {
      if (v.dataset.instyleObserved) continue;
      v.dataset.instyleObserved = 'true';
      keep(v, { wanted: () => onScreen.has(v), start: playOwn });
      io.observe(v);
    }
  };

  scan();
  document.addEventListener('shopify:section:load', (e) => scan(e.target));

  // Dawn's filters and sort replace the product grid's HTML without a page
  // load, which would leave a video card that never plays. Watch for it. The
  // cards it removes are forgotten by the sweep, so a long session of
  // filtering does not pile up dead films.
  if ('MutationObserver' in window && document.body) {
    new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((n) => {
          if (n.nodeType === 1) scan(n);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  // stats(): a read-only count for checking from the console that a long
  // session is not piling up films (kept should track the films on the page).
  const stats = () => ({ kept: kept.size, onScreen: onScreen.size, sweeping: !!sweepTimer });

  window.InStyleAutoplay = { scan, keep, stats };
})();
